import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Radio,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useGetFamilyListQuery } from '@/api/family-management/familyApi';
import {
	ClosedFamilyMemberRef,
	FamilyPageDTO,
} from '@/api/DTO/family/family.interface';
import { FamilyMemberStatusEnum } from '@/api/enum/common.enum';

interface Props {
	open: boolean;
	onClose: () => void;
	onConfirm: (row: ClosedFamilyMemberRef) => void;
}

/**
 * DMS-REQ-WEB-CHA-004 §四: picker of this centre's family members with status
 * All services closed that are not yet linked by another record (§六).
 */
export default function ClosedFamilyMemberPickerDialog({
	open,
	onClose,
	onConfirm,
}: Props) {
	const { t } = useTranslation();
	const theme = useTheme();
	const [selected, setSelected] = useState<FamilyPageDTO | null>(null);

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({ defaultValues });
	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	useEffect(() => {
		if (open) {
			setSelected(null);
			methods.reset(defaultValues);
		}
	}, [open]);

	const { data: list, isFetching } = useGetFamilyListQuery(
		{
			statusList: [FamilyMemberStatusEnum.AllServicesClosed],
			excludeLinkedClosedFamilyMember: true,
			searchString: watchSearchString,
			pagination: watchPagination,
			sortings: [],
		},
		{ refetchOnMountOrArgChange: true, skip: !open }
	);

	const columns: GridColDef<FamilyPageDTO>[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={params.row.id === selected?.id}
					onChange={() => setSelected(params.row)}
				/>
			),
		},
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 20,
			renderCell: (params) => (
				<Link
					to={`/family-management/family-management-list/detail/${params.row.id}`}
					target='_blank'
					rel='noopener noreferrer'
					onClick={(e) => e.stopPropagation()}
					style={{ color: theme.palette.primary.main }}
				>
					{params.row.familyMemberFileNo}
				</Link>
			),
		},
		{
			field: 'nameEn',
			headerName: t('family.name'),
			flex: 25,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{ field: 'phone', headerName: t('family.mobilePhone'), flex: 20 },
	];

	return (
		<Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
			<DialogTitle variant='h5'>
				{t('memberDuplication.familyPickerTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '12px' }}>
					{t('memberDuplication.familyPickerText')}
				</Typography>
				<FormProvider {...methods}>
					<Grid container spacing={2} marginTop={0} alignItems='center'>
						<Grid size={12}>
							<AppSummarySearchBar
								name='searchString'
								control={methods.control}
								placeholder={t('common.search')}
							/>
						</Grid>
						<Grid size={12}>
							<AppDataGrid
								loading={isFetching}
								rows={list?.data.items || []}
								rowCount={list?.data.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
								control={methods.control}
							/>
						</Grid>
					</Grid>
				</FormProvider>
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={onClose}>
					{t('button.cancel')}
				</Button>
				<Button
					variant='contained'
					disabled={!selected?.id}
					onClick={() =>
						selected?.id &&
						onConfirm({
							id: selected.id,
							familyMemberFileNo: selected.familyMemberFileNo,
							nameEn: selected.nameEn,
							nameZh: selected.nameZh,
						})
					}
				>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
